import React from 'react';
import { Users } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { NumberField } from '@/components/ui/number-field';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { DelegationSettings } from '@/features/delegation/types/delegation.types';

interface DelegationNotificationSettingsProps {
  settings: DelegationSettings;
  onChange: (settings: DelegationSettings, isValid: boolean) => void;
}

// ---------------------------------------------------------------------------
// Setting card — matches Submission Reminders card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({ id, label, description, control, onClick }: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg border bg-background transition-colors
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DelegationNotificationSettings: React.FC<DelegationNotificationSettingsProps> = ({
  settings,
  onChange,
}) => {
  const {
    notifyDelegateOnAssignment,
    notifyDelegateOnStart,
    notifyManagerBeforeExpiry,
    expiryReminderHoursBefore,
    notifyManagerOnDelegateAction,
  } = settings;

  const isExpiryHoursValid =
    Number.isInteger(expiryReminderHoursBefore) &&
    expiryReminderHoursBefore >= 1 &&
    expiryReminderHoursBefore <= 168;

  const handleToggle = (field: keyof DelegationSettings, checked: boolean) => {
    const updated = { ...settings, [field]: checked };
    const valid = !updated.notifyManagerBeforeExpiry || isExpiryHoursValid;
    onChange(updated, valid);
  };

  const handleExpiryHoursChange = (value: number) => {
    const updated = { ...settings, expiryReminderHoursBefore: value };
    const valid = Number.isInteger(value) && value >= 1 && value <= 168;
    onChange(updated, !updated.notifyManagerBeforeExpiry || valid);
  };

  const showExpiryHoursError = notifyManagerBeforeExpiry && !isExpiryHoursValid;

  return (
    <Accordion type="multiple" defaultValue={['delegation-notifications']} className="space-y-4">
      <AccordionItem value="delegation-notifications" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Delegation Notifications</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure notifications for delegation events
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4 space-y-6">

          {/* Delegate notifications subgroup */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Delegate notifications</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <SettingCard
                id="notifyDelegateOnAssignment"
                label="Notify delegate when assigned"
                description="Send a platform feed notification to the delegate when a manager assigns them"
                control={
                  <Checkbox
                    id="notifyDelegateOnAssignment"
                    checked={notifyDelegateOnAssignment}
                    onCheckedChange={(v) => handleToggle('notifyDelegateOnAssignment', !!v)}
                  />
                }
                onClick={() => handleToggle('notifyDelegateOnAssignment', !notifyDelegateOnAssignment)}
              />
              <SettingCard
                id="notifyDelegateOnStart"
                label="Notify delegate when delegation starts"
                description="Send a platform feed notification to the delegate on the day their delegation begins"
                control={
                  <Checkbox
                    id="notifyDelegateOnStart"
                    checked={notifyDelegateOnStart}
                    onCheckedChange={(v) => handleToggle('notifyDelegateOnStart', !!v)}
                  />
                }
                onClick={() => handleToggle('notifyDelegateOnStart', !notifyDelegateOnStart)}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t" />

          {/* Manager notifications subgroup */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Manager notifications</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <SettingCard
                id="notifyManagerBeforeExpiry"
                label="Notify manager before delegation expires"
                description="Send a platform feed notification to the manager before their delegation expires"
                control={
                  <Checkbox
                    id="notifyManagerBeforeExpiry"
                    checked={notifyManagerBeforeExpiry}
                    onCheckedChange={(v) => handleToggle('notifyManagerBeforeExpiry', !!v)}
                  />
                }
                onClick={() => handleToggle('notifyManagerBeforeExpiry', !notifyManagerBeforeExpiry)}
              />
              <SettingCard
                id="notifyManagerOnDelegateAction"
                label="Notify manager when delegate takes action"
                description="Send a platform feed notification to the manager when their delegate approves or returns a timesheet on their behalf"
                control={
                  <Checkbox
                    id="notifyManagerOnDelegateAction"
                    checked={notifyManagerOnDelegateAction}
                    onCheckedChange={(v) => handleToggle('notifyManagerOnDelegateAction', !!v)}
                  />
                }
                onClick={() => handleToggle('notifyManagerOnDelegateAction', !notifyManagerOnDelegateAction)}
              />
            </div>

            {/* Expiry hours input — only shown when checkbox is on */}
            {notifyManagerBeforeExpiry && (
              <div className="pl-4 border-l-2 border-muted space-y-2">
                <Label htmlFor="expiryReminderHoursBefore">
                  Notify how many hours before expiry
                </Label>
                <div className="flex gap-2">
                  <NumberField
                    id="expiryReminderHoursBefore"
                    min={1}
                    max={168}
                    clampOnBlur={false}
                    value={expiryReminderHoursBefore}
                    onChange={handleExpiryHoursChange}
                    className={`h-10 w-20${showExpiryHoursError ? ' border-destructive focus-visible:ring-destructive' : ''}`}
                    aria-describedby={showExpiryHoursError ? 'expiry-hours-error' : 'expiry-hours-hint'}
                    aria-invalid={showExpiryHoursError}
                  />
                  <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                    hours
                  </div>
                </div>

                {showExpiryHoursError ? (
                  <p id="expiry-hours-error" className="text-xs text-destructive">
                    Please enter a value between 1 and 168.
                  </p>
                ) : (
                  <p id="expiry-hours-hint" className="text-xs text-muted-foreground">
                    How many hours before expiry the manager will be notified (max 168 hours / 7 days)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Cross-link note */}
          <p className="text-xs text-muted-foreground border-t pt-4">
            These settings are shared with the Delegation Settings page.
          </p>

        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
